export interface ValidationStatusRatio {
  readonly passed: number;
  readonly total: number;
}

// Compound validationStatus tokens look like `${prefix}_${statusValue}`
// where the status shapes DACS emits are `_PASSED`, `_PASSED_WITH_EXCEPTIONS`,
// `_FAILED`, `_NOT_APPLICABLE`, and `_NO_DATA` (see SearchSideBar-config
// VALIDATION_STATUS_ITEMS_* and statusOverview/chartData.ts). The ratio
// should reflect only pipelines that actually ran, so `_NOT_APPLICABLE`
// and `_NO_DATA` are excluded from both numerator and denominator.
// Exact suffix matches also protect the counters from unrelated tokens
// that merely contain "PASS" as a substring.
const PASS_SUFFIXES: readonly string[] = ["_PASSED", "_PASSED_WITH_EXCEPTIONS"];
const RAN_SUFFIXES: readonly string[] = [...PASS_SUFFIXES, "_FAILED"];

function hasAnySuffix(token: string, suffixes: readonly string[]): boolean {
  return suffixes.some((suffix) => token.endsWith(suffix));
}

export function parseValidationStatus(
  raw: string | null | undefined
): ValidationStatusRatio | undefined {
  if (typeof raw !== "string" || raw.length === 0) {
    return undefined;
  }

  const tokens = raw
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

  const ran = tokens.filter((token) => hasAnySuffix(token, RAN_SUFFIXES));
  if (ran.length === 0) {
    return undefined;
  }

  const total = ran.length;
  const passed = ran.filter((token) =>
    hasAnySuffix(token, PASS_SUFFIXES)
  ).length;

  return { passed, total };
}
